import { Injectable } from '@angular/core';
import { PermissionState, registerPlugin } from '@capacitor/core';
import { StorageService } from '../commons/storage.service';
import { GlobalVariablesService } from '../global-variables.service';
import { Installation } from '../../models/Installation';
import { GlobalMethodsService } from '../global-methods.service';
import { GeoAroundService } from '../commons/geo-around.service';
import { NotificationGeoAroundRequest } from '../SicorService/RequestObject/NotificationGeoAroundRequest';
import { Diagnostic } from '@ionic-native/diagnostic/ngx';
import { Platform } from '@ionic/angular';
import { TranslateConfigService } from 'src/app/services/commons/translate-config.service';
import { Geolocation } from '@capacitor/geolocation';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';
import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { Device } from '@capacitor/device';
const LOCATION = 'location';
const GeoPlugin = registerPlugin<any>('GeoPlugin');
const GeoPluginiOS = registerPlugin<any>('GeoPluginiOS');
@Injectable({
  providedIn: 'root'
})

export class LocationService {
  public EarthRadius = 6371;
  public baseCoords = { latitude: 0, longitude: 0 };

  constructor(private storage: StorageService,
    public gvs: GlobalVariablesService,
    public gms: GlobalMethodsService,
    private geoAroundService: GeoAroundService,
    private diagnostic: Diagnostic,
    private platform: Platform,
    private translate: TranslateConfigService,
    private androidPermissions: AndroidPermissions
  ) { }

  async updateCurrentPosition(permitNeeded: string) {
    if (await this.platform.is('mobileweb') || await this.platform.is('desktop')) {
      await this.storage.setObject(LOCATION, this.baseCoords);
      return;
    }
    let permission = await this.alwaysPermissionsGranted();
    if (permission === this.diagnostic.permissionStatus.DENIED_ALWAYS) {
      await this.storage.setObject(LOCATION, this.baseCoords);
      return;
    }
    if (permitNeeded === 'always') {
      if (await this.platform.is('android')) {
        let infoDevice = await Device.getInfo();
        if ((await infoDevice.androidSDKVersion > 29 && permission != this.diagnostic.permissionStatus.GRANTED) ||
          (await infoDevice.androidSDKVersion <= 29 && permission != 'authorized_when_in_use')) {
            this.requestLocationPermissions('always');
        }
      }
      if (await this.platform.is('ios')) {
        if (permission != this.diagnostic.permissionStatus.GRANTED) {
          this.requestLocationPermissions('always');
        }
      }
    } else if (permitNeeded === 'generic') {
      if (permission != this.diagnostic.permissionStatus.GRANTED && permission != this.diagnostic.permissionStatus.GRANTED_WHEN_IN_USE) {
        this.requestLocationPermissions('generic');
      }
    }
  }

  async requestLocationPermissions(permitNeeded: string) {
    if (await this.platform.is('mobileweb') || await this.platform.is('desktop')) {
      return;
    }
    const modal = await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleInfo'), await this.translate.getTranslate('MESSAGES.permgpsText'),
      'modal-default', await this.translate.getTranslate('BUTTON.next'));
    modal.onDidDismiss().then(async res => {
      if (res.data != null && res.data.result) {
        await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
        let permission = await this.alwaysPermissionsGranted();
        let infoDevice = await Device.getInfo();
        if(permission.toUpperCase() !== 'DENIED_ONCE' && permission.toUpperCase() != 'DENIED_ALWAYS'){
          if (permitNeeded === 'always') {
            if (await this.platform.is('android')) {
              if((infoDevice.androidSDKVersion > 29) || (infoDevice.androidSDKVersion <=29 && permission !== 'authorized_when_in_use')){
                await GeoPlugin.openSettingsScreen();
              }
            } else if (await this.platform.is('ios')) {
              await GeoPluginiOS.openSettingsScreen();
            }
          }
          if (await this.platform.is('android')) {
            await this.updateLocationAndroid();
          } else if (await this.platform.is('ios')) {
            await this.updateLocationiOS();
          }
        }
      }
    });
  }

  async alwaysPermissionsGranted(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      let statusPermission = await this.diagnostic.getLocationAuthorizationStatus();
      resolve(statusPermission);
    });
  }

  async isPermissionAlwaysGranted() {
    return new Promise(async (resolve, reject) => {
      //let permissions = navigator.permissions.query({name: 'geolocation'});
      if (await this.platform.is('ios')) {
        await this.diagnostic.getLocationAuthorizationStatus().then(async value => {
          if (value === this.diagnostic.permissionStatus.GRANTED) {
            resolve(true);
          } else {
            resolve(false);
          }
        }).catch(async err => {
          const message = JSON.stringify(err);
          await FirebaseCrashlytics.recordException({ message });
          reject(false);
        })
      }
      if (await this.platform.is('android')) {
        let permission = await this.alwaysPermissionsGranted();
        let infoDevice = await Device.getInfo();
        if ((await infoDevice.androidSDKVersion > 29 && permission === this.diagnostic.permissionStatus.GRANTED) ||
          (await infoDevice.androidSDKVersion <= 29 && permission === 'authorized_when_in_use')) {
          resolve(true);
        } else {
          resolve(false);
        }
      }
    });
  }

  async updateLocation() {
    if (await this.platform.is('android')) {
      await this.updateLocationAndroid();
    }
    else if (await this.platform.is('ios')) {
      await this.updateLocationiOS();
    }
  }

  async updateLocationAndroid() {
    let coords = {
      latitude: 0,
      longitude: 0,
    };
    await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 1000 }).then(async coordinates => {
      coords = {
        latitude: coordinates.coords.latitude,
        longitude: coordinates.coords.longitude,
      };
      await this.storage.setObject(LOCATION, coords);
    }).catch(async err => {
      const message = JSON.stringify(err);
      await FirebaseCrashlytics.recordException({ message: message });
      await this.storage.setObject(LOCATION, this.baseCoords);
    });
  }

  async updateLocationiOS() {
    let coords = {
      latitude: 0,
      longitude: 0,
    };
    try {
      const internalGetCurrentPosition = async (options: PositionOptions = {}): Promise<GeolocationPosition> => {
        return new Promise<GeolocationPosition>(async (resolve, reject) => {
          const id = await Geolocation.watchPosition(options, async (position, err) => {
            Geolocation.clearWatch({ id });
            if (err) {
              await this.storage.setObject(LOCATION, position);
              reject(err);
              return;
            }
            resolve(position);
          });
        });
      };
      await internalGetCurrentPosition({ enableHighAccuracy: true, timeout: 10000 }).then((resp) => {
        if (resp != null && resp.coords != null) {
          coords.latitude = resp.coords.latitude;
          coords.longitude = resp.coords.longitude;
        }
      }).catch(async (error) => {
        const message = JSON.stringify(error);
        await FirebaseCrashlytics.recordException({ message: message });
        coords.latitude = 0;
        coords.longitude = 0;
      });
      await this.storage.setObject(LOCATION, coords);
    } catch (err) {
      const message = JSON.stringify(err);
      await FirebaseCrashlytics.recordException({ message: message });
      await this.storage.setObject(LOCATION, coords);
    }
  }

  async checkGeofenceProblems(){
    if (!await this.platform.is('mobileweb') && !await this.platform.is('desktop')) {
      let isAlwaysGranted = await this.isPermissionAlwaysGranted();
      const confInstallations = await this.storage.getObject(this.gvs.CONF_INSTALLATIONS + '_' + this.gvs.user.userName);
      const installation = confInstallations.find(x => x.s_ins === this.gvs.currentInstallation.s_ins);
      if (!isAlwaysGranted && installation.geoAround) {
        this.gvs.geofenceProblems = true;
      } else {
        this.gvs.geofenceProblems = false;
      }
    }
  }
}